import { Breadcrumbs, Typography, AppBar, Tabs, Tab, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Switch, FormControlLabel, Avatar } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { http, useNavigationService, AlertService, FxSelect, FxInput, formatService, general } from '../fx-core';
import './LoyaltyPoints.scss';
import * as _ from 'lodash';

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function LoyaltyPoints() {
    const { routeData, back } = useNavigationService();
    const [tabValue, setTabValue] = useState(0);

    // Cascading state
    const [communityList, setCommunityList] = useState<any[]>([]);
    const [stalls, setStalls] = useState<any[]>([]);
    const [stallItems, setStallItems] = useState<any[]>([]);

    // Selections
    const [selCommunityId, setSelCommunityId] = useState(0);
    const [selStallId, setSelStallId] = useState(0);
    const [selItemId, setSelItemId] = useState(0);

    // Form state
    const [loyaltyPoints, setLoyaltyPoints] = useState('');
    const [stock, setStock] = useState('');
    const [availableStock, setAvailableStock] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [editId, setEditId] = useState(0);
    const [selectedPrice, setSelectedPrice] = useState(0);

    // Data lists
    const [loyaltyProducts, setLoyaltyProducts] = useState<any[]>([]);
    const [activeProducts, setActiveProducts] = useState<any[]>([]); // For Active Products tab
    const [adminClaimHistory, setAdminClaimHistory] = useState<any[]>([]); // For Admin Claim History
    const [pointsSummary, setPointsSummary] = useState<any>(null);

    useEffect(() => {
        getTier4CommunityList();
    }, []);

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabValue(newValue);
        if (newValue === 1) getActiveProducts();
        if (newValue === 2) getAdminClaimHistory();
        if (newValue === 3) getPointsSummary();
    };

    const getTier4CommunityList = () => {
        let apiOptions = {
            url: 'community/parent/list',
            data: { communityId: routeData.communityId }
        };
        http.post(apiOptions).then(response => {
            setCommunityList(response.data || []);
            // Default to current community if in list
            if (routeData.communityId) setSelCommunityId(routeData.communityId);
        });
    };

    useEffect(() => {
        if (selCommunityId > 0) {
            initLookup(selCommunityId);
            GetLoyaltyProducts(selCommunityId);
        }
    }, [selCommunityId]);

    const initLookup = (communityId) => {
        let inputData = {
            lookups: { Stalls: { default: true } },
            filters: { communityId: communityId, tier4Flag: true }
        };
        http.post({ url: 'options/lookup', data: inputData }).then(response => {
            setStalls(response.data.Stalls || []);
        });
    };

    useEffect(() => {
        if (selStallId > 0) {
            getStallItemsByStallId(selStallId);
        }
    }, [selStallId]);

    const getStallItemsByStallId = (stallId) => {
        let inputData = {
            filters: {
                stallId: stallId,
                currentDate: formatService.getCurrentDateForServer()
            }
        };
        http.post({ url: 'portal/stallitembatch/itemsbystall/itemsales', data: inputData }).then(response => {
            setStallItems(response.data || []);
        });
    };

    const handleItemChange = (itemId) => {
        setSelItemId(itemId);
        const selected = stallItems.find(x => x.stallitem.id === itemId);
        if (selected) {
            setSelectedPrice(selected.stalltemBatch?.price || 0);
            // We do NOT fetch or set stock automatically as per requirements
        }
    };

    const resetForm = () => {
        setSelItemId(0);
        setLoyaltyPoints('');
        setStock('');
        setAvailableStock(0);
        setIsActive(true);
        setEditId(0);
        setSelectedPrice(0);
    };

    const onSetupProduct = () => {
        if (!selItemId || !loyaltyPoints || !stock) {
            AlertService.showMsg({ message: "Please fill all fields", variant: "error" });
            return;
        }

        const selectedItem = stallItems.find(x => x.stallitem.id === selItemId);

        const inputData = {
            id: editId,
            communityId: selCommunityId,
            stallId: selStallId,
            itemId: selItemId,
            itemName: selectedItem?.stallitem?.itemName || "",
            loyaltyPoints: parseFloat(loyaltyPoints),
            stock: parseInt(stock),
            isActive: isActive
        };

        http.post({ url: 'loyalty/setup', data: inputData }).then(response => {
            AlertService.showMsg({ message: editId > 0 ? "Product updated successfully" : "Product setup successfully" });
            resetForm();
            GetLoyaltyProducts(selCommunityId);
        });
    };

    const GetLoyaltyProducts = (communityId) => {
        http.get({ url: `loyalty/list` }).then(response => {
            setLoyaltyProducts(response.data || []);
        });
    };

    const onEdit = (row) => {
        setEditId(row.id);
        setSelStallId(row.stallId);
        // We'll need to wait for items to load or find it if already loaded
        setSelItemId(row.itemId);
        setLoyaltyPoints(row.loyaltyPoints.toString());
        setStock(row.stock.toString());
        setIsActive(row.isActive);
        setTabValue(0); // Go to setup tab
    };

    const onDelete = (id) => {
        http.delete({ url: `loyalty/delete/${id}` }).then(() => {
            AlertService.showMsg({ message: "Product deleted successfully" });
            GetLoyaltyProducts(selCommunityId);
        });
    };

    const getActiveProducts = () => {
        http.get({ url: `loyalty/products/list` }).then(response => {
            setActiveProducts(response.data || []);
        });
    };

    const getAdminClaimHistory = () => {
        http.get({ url: `loyalty/admin/claim-history` }).then(response => {
            setAdminClaimHistory(response.data || []);
        });
    };

    const getPointsSummary = () => {
        http.get({ url: 'loyalty/admin/claim-history' }).then(response => {
            setPointsSummary(response.data);
        });
    };

    return (
        <div className='stallcouponlist-ui pt-4 mt-3'>
            <div className="title-font custom-head pl-4 pt-5">
                <Breadcrumbs aria-label="breadcrumb">
                    <span className="custom-link pointer breadcrumbs-style" onClick={() => back()}>
                        <b>{routeData.communityName || 'Dashboard'}</b>
                    </span>
                    <Typography color="primary" className="breadcrumbs-style">Loyalty Points Admin</Typography>
                </Breadcrumbs>
            </div>

            <div className="p-4">
                <div className="row mb-4">
                    <div className="col-md-4">
                        <FxSelect
                            label="Select Community"
                            options={communityList.map(c => ({ id: c.id, text: c.name }))}
                            selectedValue={selCommunityId}
                            valueField="id"
                            displayField="text"
                            onValueChange={(val) => setSelCommunityId(val.value)}
                        />
                    </div>
                </div>

                <AppBar position="static" color="default" className="premium-tabs">
                    <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="fullWidth">
                        <Tab label="Setup" />
                        <Tab label="Active" />
                        <Tab label="Claim History" />
                        {/* <Tab label="My Points" /> */}
                    </Tabs>
                </AppBar>

                <TabPanel value={tabValue} index={0}>
                    <Paper className="p-4 mb-4">
                        <Typography variant="h6" className="mb-3">{editId > 0 ? 'Edit' : 'Add New'} Loyalty Product</Typography>
                        <div className="row">
                            <div className="col-md-4">
                                <FxSelect
                                    label="Select Stall"
                                    options={stalls}
                                    selectedValue={selStallId}
                                    valueField="id"
                                    displayField="text"
                                    onValueChange={(val) => setSelStallId(val.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <FxSelect
                                    label="Select Item"
                                    options={stallItems.map(i => ({ id: i.stallitem.id, text: i.stallitem.itemName }))}
                                    selectedValue={selItemId}
                                    valueField="id"
                                    displayField="text"
                                    onValueChange={(val) => handleItemChange(val.value)}
                                    disabled={!selStallId}
                                />
                            </div>
                            <div className="col-md-2">
                                <FxInput
                                    label="Points Required"
                                    type="number"
                                    value={loyaltyPoints}
                                    onValueChange={(val) => setLoyaltyPoints(val.value)}
                                />
                            </div>
                            <div className="col-md-2">
                                <FxInput
                                    label="Total Stock"
                                    type="number"
                                    value={stock}
                                    onValueChange={(val) => setStock(val.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <Box className="item-info-display d-flex justify-content-center align-items-center" style={{ height: '40px', marginTop: '24px' }}>
                                    <span className="premium-price-tag">₹ {selectedPrice}</span>
                                </Box>
                            </div>
                        </div>
                        <div className="row mt-3 align-items-center">
                            <div className="col-md-4">
                                <FormControlLabel
                                    control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
                                    label="Active"
                                />
                            </div>
                            <div className="col-md-8 text-right">
                                {editId > 0 && <Button onClick={resetForm} className="mr-2">Cancel</Button>}
                                <Button variant="contained" color="primary" onClick={onSetupProduct}>
                                    {editId > 0 ? 'Update Product' : 'Setup Product'}
                                </Button>
                            </div>
                        </div>
                    </Paper>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Image</TableCell>
                                    <TableCell>Item Name</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell>Points</TableCell>
                                    <TableCell>Total Stock</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loyaltyProducts.map((row: any) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <Avatar variant="rounded" src={general.getImageUrl(row.imagePath)} alt={row.itemName} />
                                        </TableCell>
                                        <TableCell>{row.itemName}</TableCell>
                                        <TableCell>₹{row.price}</TableCell>
                                        <TableCell>{row.loyaltyPoints}</TableCell>
                                        <TableCell>{row.stock}</TableCell>
                                        <TableCell>{row.isActive ? 'Active' : 'Inactive'}</TableCell>
                                        <TableCell>
                                            <Button size="small" color="primary" onClick={() => onEdit(row)}>Edit</Button>
                                            <Button size="small" color="primary" onClick={() => onDelete(row.id)}>Delete</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loyaltyProducts.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">No products setup for this community.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <TableContainer component={Paper} className="premium-table">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Image</TableCell>
                                    <TableCell>Product Name</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell>Points</TableCell>
                                    <TableCell>Total Stock</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {activeProducts.map((row: any) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <Avatar variant="rounded" src={general.getImageUrl(row.imagePath)} alt={row.itemName} />
                                        </TableCell>
                                        <TableCell>{row.itemName}</TableCell>
                                        <TableCell>₹{row.price}</TableCell>
                                        <TableCell>{row.loyaltyPoints}</TableCell>
                                        <TableCell>{row.stock}</TableCell>
                                    </TableRow>
                                ))}
                                {activeProducts.length === 0 && (
                                    <TableRow><TableCell colSpan={5} align="center">No Active Products found.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <TableContainer component={Paper} className="premium-table">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Item Name</TableCell>
                                    <TableCell>Points</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Order ID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {adminClaimHistory.map((row: any) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.userName}</TableCell>
                                        <TableCell>{row.itemName}</TableCell>
                                        <TableCell>{row.pointsUsed}</TableCell>
                                        <TableCell>{formatService.getConvertedDate(row.createdDate)}</TableCell>
                                        <TableCell>#{row.orderId}</TableCell>
                                    </TableRow>
                                ))}
                                {adminClaimHistory.length === 0 && (
                                    <TableRow><TableCell colSpan={5} align="center">No claim history found.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    {pointsSummary ? (
                        <div className="text-center p-5">
                            <Typography variant="h4" color="primary">Your Current Points: {pointsSummary.points}</Typography>
                            <Typography variant="subtitle1" className="mt-4">Point Transactions History</Typography>
                            <TableContainer component={Paper} className="mt-2" style={{ maxWidth: '600px', margin: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Points</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pointsSummary.history?.map((h: any) => (
                                            <TableRow key={h.id}>
                                                <TableCell>{formatService.getConvertedDate(h.createdDate)}</TableCell>
                                                <TableCell>{h.DelizA}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    ) : (
                        <Typography align="center">Loading points summary...</Typography>
                    )}
                </TabPanel>
            </div>
        </div>
    );
}
